import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

import ActionBar from '../../../../../../../shared/components/v3/ActionBar/ActionBar';
import BdsCircleIndicatorPictogram from '../../../../../../../shared/components/v3/BdsCircleIndicator/BdsCircleIndicatorPictogram/BdsCircleIndicatorPictogram';
import StatusCard from '../../../../../../../shared/components/v3/Cards/StatusCard/StatusCard';
import StyledTable from '../../../../../../../shared/components/v3/Table/StyledTable/StyledTable';
import TablePaginationV3 from '../../../../../../../shared/components/v3/TablePagination/TablePagination';
import Toast from '../../../../../../../shared/components/v3/Toast/Toast';
import {
  DEFAULT_PAGINATOR_OPTIONS_V3,
  DEFAULT_PAGINATOR_STATE_V3
} from '../../../../../../../shared/constants/constants';
import { CHECK_ICON_V2 } from '../../../../../../../shared/constants/images';
import { MANAGE_COLLABORATORS_SECTION } from '../../../../../../../shared/constants/tagManager';
import { DELETE_COLLABORATOR_SUCCESS_TOAST } from '../../../../../../../shared/constants/toast';
import {
  COLLABORATORS_WITH_ERROR_COLUMN,
  COLLABORATORS_WITH_ERROR_DOC_TYPE_COLUMN,
  COLLABORATORS_WITH_ERROR_EXTRA_COLUM,
  COLLABORATORS_WITH_ERROR_GENDER_COLUMN
} from '../../../../../../../shared/handlers/tableColumns';
import type { IMemberByFileData } from '../../../../../../../shared/interfaces/manageCollaborators';
import type { ISelectedMap } from '../../../../../../../shared/interfaces/tables';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../../../../shared/translations/manageCollaborators';
import { isEmptyObject } from '../../../../../../../shared/utils/validation/validation';
import { hasErrors } from '../../utils/hasErrorField';
import './SubscriptionByFileTable.scss';

interface ISubscriptionByFileTable {
  planCountryCode: string;
  isFR: boolean;
  membersWithError: IMemberByFileData[];
  setMemberToUpload: (members: IMemberByFileData[]) => void;
  setReadyToUpload: (ready: boolean) => void;
}

const SubscriptionByFileTable = ({
  planCountryCode,
  isFR,
  membersWithError,
  setMemberToUpload,
  setReadyToUpload
}: ISubscriptionByFileTable) => {
  const [collaboratorsReference, setCollaboratorsReference] = useState<IMemberByFileData[]>();
  const [collaboratorsUpdated, setCollaboratorsUpdated] = useState<IMemberByFileData[]>([]);
  const [collaboratorsDeleted, setCollaboratorsDeleted] = useState<IMemberByFileData[]>([]);
  const [lastUpdatedCollaborator, setLastUpdatedCollaborator] = useState<IMemberByFileData | null>(null);
  const [collaboratorsJustDeleted, setCollaboratorsJustDeleted] = useState<boolean>(false);
  const [currentPageData, setCurrentPageData] = useState<IMemberByFileData[]>([]);

  const [collaboratorsWithErrors, setCollaboratorsWithErrors] = useState<number>(0);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE_V3);
  const [actualPage, setActualPage] = useState<number>(0);
  const prevCollaboratorsWithErrorsRef = useRef<number>(0);

  const [selectedMap, setSelectedMap] = useState<ISelectedMap>({});
  const [allSelected, setAllSelected] = useState(false);
  const [totalSelected, setTotalSelected] = useState(0);
  const [openToast, setOpenToast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPagination, setloadingPagination] = useState(false);

  const tableRef = React.createRef<HTMLDivElement>();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const exluyeGender = planCountryCode === 'br';
  const docTypeAvaliable = ['co', 'pe', 'es'];
  const excludeSecondLastName = planCountryCode.toLocaleLowerCase() === 'us';

  const incluideDocType = docTypeAvaliable.includes(planCountryCode.toLowerCase());

  const columns = useMemo(() => {
    const docTypeColumn = incluideDocType ? [COLLABORATORS_WITH_ERROR_DOC_TYPE_COLUMN] : [];
    const genderColumn = exluyeGender ? [] : COLLABORATORS_WITH_ERROR_GENDER_COLUMN;
    const extraColumn = isFR ? COLLABORATORS_WITH_ERROR_EXTRA_COLUM : [];
    const colabColumns = [...COLLABORATORS_WITH_ERROR_COLUMN].filter((column) => {
      if (excludeSecondLastName) {
        return column.key !== 'secondLastName';
      }
      return true;
    });

    const generalColumn = [...docTypeColumn, ...colabColumns, ...genderColumn, ...extraColumn];

    return generalColumn;
  }, [isFR, incluideDocType, exluyeGender, excludeSecondLastName]);

  const insertDash = useCallback((value: string, index: number) => {
    return `${value}/${index}`;
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    if (target.tagName === 'INPUT' || target.isContentEditable) {
      return;
    }

    if (!tableRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - tableRef.current.offsetLeft);
    setScrollLeft(tableRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !tableRef.current) return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.isContentEditable) {
      setIsDragging(false);
      return;
    }
    e.preventDefault();
    const x = e.pageX - tableRef.current.offsetLeft;
    const walk = startX - x;
    tableRef.current.scrollLeft = scrollLeft + walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    setLoading(true);

    const timerId = setTimeout(() => {
      setCollaboratorsUpdated(membersWithError);
      setCollaboratorsReference(membersWithError);
      setLoading(false);
    }, 10);

    return () => {
      clearTimeout(timerId);
    };
  }, [membersWithError]);

  const totalCollaborators = useMemo(() => {
    return collaboratorsUpdated ? collaboratorsUpdated.length : 0;
  }, [collaboratorsUpdated]);

  const updateMemberToUpload = useCallback(
    (collaborators: IMemberByFileData[]) => {
      const collaboratorsWithoutErrors = collaborators.filter((collaborator) => !hasErrors(collaborator));
      setMemberToUpload(collaboratorsWithoutErrors);
    },
    [setMemberToUpload]
  );

  useEffect(() => {
    if (!collaboratorsUpdated) return;

    const newCollaboratorsWithErrors = collaboratorsUpdated.filter(hasErrors);
    const newCollaboratorsWithErrorsCount = newCollaboratorsWithErrors.length;
    setCollaboratorsWithErrors(newCollaboratorsWithErrorsCount);

    if (prevCollaboratorsWithErrorsRef.current !== newCollaboratorsWithErrorsCount) {
      setReadyToUpload(newCollaboratorsWithErrorsCount === 0);
      prevCollaboratorsWithErrorsRef.current = newCollaboratorsWithErrorsCount;
    }

    if (lastUpdatedCollaborator && !hasErrors(lastUpdatedCollaborator)) {
      updateMemberToUpload(collaboratorsUpdated);
      setLastUpdatedCollaborator(null);
    } else if (collaboratorsJustDeleted) {
      updateMemberToUpload(collaboratorsUpdated);
      setCollaboratorsJustDeleted(false);
    }
  }, [collaboratorsUpdated, lastUpdatedCollaborator, collaboratorsJustDeleted, updateMemberToUpload, setReadyToUpload]);

  const handleOnChangeField = (field: string, updatedMember: IMemberByFileData) => {
    setCollaboratorsUpdated((prev) =>
      prev.map((member, index) => (index === Number(field.split('/')[1]) ? updatedMember : member))
    );
    setLastUpdatedCollaborator(updatedMember);
  };

  const onCheckRow = useCallback((key: string) => {
    setSelectedMap((prevMap) => {
      const newMap = { ...prevMap };
      if (newMap[String(key)]) {
        delete newMap[String(key)];
      } else {
        newMap[String(key)] = true;
      }
      return newMap;
    });
  }, []);

  const onCheckAll = useCallback(() => {
    if (allSelected) {
      setSelectedMap({});
    } else {
      const newMap = collaboratorsUpdated?.reduce((map: ISelectedMap, obj, index) => {
        const key = insertDash(obj.docValue.value, index);
        map[key] = true;
        return map;
      }, {});
      setSelectedMap(newMap ?? {});
    }
    setAllSelected(!allSelected);
  }, [allSelected, collaboratorsUpdated, insertDash]);

  useEffect(() => {
    const selected = Object.values(selectedMap).filter(Boolean).length;
    setTotalSelected(selected);
  }, [selectedMap]);

  const handleDeleteRow = useCallback(() => {
    if (collaboratorsUpdated) {
      const selectedCollaborators = Object.keys(selectedMap).filter((key) => selectedMap[key]);
      setCollaboratorsUpdated((prev) => {
        const remainingCollaborators = prev.filter(
          (collaborator, index) => !selectedCollaborators.includes(insertDash(collaborator.docValue.value, index))
        );
        setCollaboratorsDeleted(
          prev.filter((collaborator, index) =>
            selectedCollaborators.includes(insertDash(collaborator.docValue.value, index))
          )
        );
        return remainingCollaborators;
      });

      setSelectedMap((prevMap) => {
        const newMap = { ...prevMap };
        selectedCollaborators.forEach((key) => {
          delete newMap[key];
        });
        return newMap;
      });
      setAllSelected(false);
      setOpenToast(true);
      setCollaboratorsJustDeleted(true);
    }
  }, [collaboratorsUpdated, selectedMap, insertDash]);

  const handleUndoDelete = () => {
    if (!collaboratorsDeleted.length || !collaboratorsReference) return;

    setCollaboratorsUpdated((prev) => {
      const updated = prev ? [...prev] : [];
      const restoredCollaborators = restoreDeletedCollaborators(updated);
      return restoredCollaborators;
    });

    setOpenToast(false);
    setCollaboratorsDeleted([]);
    setCollaboratorsJustDeleted(true);
  };

  const restoreDeletedCollaborators = (updated: IMemberByFileData[]) => {
    collaboratorsDeleted.forEach((deletedCollaborator) => {
      const index = findCollaboratorIndex(deletedCollaborator);
      if (index > -1) {
        updated.splice(index, 0, deletedCollaborator);
      } else {
        updated.unshift(deletedCollaborator);
      }
    });
    return updated;
  };

  const findCollaboratorIndex = (deletedCollaborator: IMemberByFileData) => {
    if (!collaboratorsReference) return -1;
    return collaboratorsReference.findIndex(
      (collaborator) => collaborator.docValue.value === deletedCollaborator.docValue.value
    );
  };

  const paginateCollaborators = useCallback(() => {
    const from = actualPage * paginatorState;
    const to = from + paginatorState;
    const pageData = collaboratorsUpdated?.slice(from, to);

    setCurrentPageData(pageData || []);
    setloadingPagination(false);
  }, [actualPage, paginatorState, collaboratorsUpdated]);

  const handleOnPageChanged = (page: any) => {
    setActualPage(page);
    setloadingPagination(true);
  };

  const handleOnRowsPerPageChanged = (page: any) => {
    setPaginatorState(page);
    setloadingPagination(true);
  };

  useEffect(() => {
    if (collaboratorsUpdated) {
      paginateCollaborators();
    }
  }, [collaboratorsUpdated, paginateCollaborators]);

  const handleCloseToast = () => {
    setOpenToast(false);
    setCollaboratorsDeleted([]);
  };

  const closeActionBar = useCallback(() => {
    setSelectedMap({});
    setAllSelected(false);
    setTotalSelected(0);
  }, []);

  const renderToast = () => {
    return (
      <Toast
        description={t(
          TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.toast.deleteSucess,
          { count: collaboratorsDeleted.length }
        )}
        open={openToast}
        button={DELETE_COLLABORATOR_SUCCESS_TOAST.button}
        buttonText={t(DELETE_COLLABORATOR_SUCCESS_TOAST.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
        secondaryActionData={{
          action: handleUndoDelete,
          text: t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.toast.button)
        }}
        icon={DELETE_COLLABORATOR_SUCCESS_TOAST.icon}
      />
    );
  };

  const renderStatusCard = useCallback(() => {
    return (
      <StatusCard
        type="error"
        message={
          <Trans
            i18nKey={TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.statusCard}
            components={{
              b: <b />
            }}
            count={collaboratorsWithErrors}
            values={{
              rows: collaboratorsWithErrors
            }}
          />
        }
      />
    );
  }, [collaboratorsWithErrors]);

  if (collaboratorsWithErrors < 1 && !loading) {
    return (
      <div className="without-errors flex flex-col justify-start items-start">
        <BdsCircleIndicatorPictogram
          circleIndicatorSize="l"
          className="pictogram"
          pictogram={{
            component: CHECK_ICON_V2
          }}
        />

        <div className="info flex flex-col justify-start items-start">
          <div className="title">
            {t(
              TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.withoutErrors.title
            )}
          </div>
          <div className="subtitle">
            {t(
              TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.withoutErrors
                .description
            )}
          </div>
        </div>
        {renderToast()}
      </div>
    );
  }

  return (
    <div className="subscription-by-file-container">
      {loading && <Skeleton className="w-full" animation={false} height={70} />}
      {collaboratorsWithErrors >= 1 && !loading && <div className="info-row w-full">{renderStatusCard()}</div>}
      <div
        className="subscription-by-file-table w-full"
        data-actual-page={actualPage}
        data-rows-per-page={paginatorState}
        ref={tableRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseDown={handleMouseDown}
      >
        <StyledTable
          data={currentPageData}
          columns={columns}
          error={false}
          loading={loading || loadingPagination}
          dataKey="docValue"
          onChange={handleOnChangeField}
          checkProps={{ onCheckRow, onCheckAll, checkedRows: selectedMap, allSelected }}
          showShadowHeader={false}
        />
        {collaboratorsUpdated && (
          <div className="paginator-container">
            <TablePaginationV3
              marginTop={40}
              rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
              rowsPerPage={paginatorState}
              count={totalCollaborators}
              actualPage={actualPage}
              onPageChange={handleOnPageChanged}
              onRowsPerPageChange={handleOnRowsPerPageChanged}
              sectionPage={MANAGE_COLLABORATORS_SECTION}
            />
          </div>
        )}
        {selectedMap && !isEmptyObject(selectedMap) && (
          <div className="subscription-by-file-table-action-bar">
            <ActionBar
              text={t(
                TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.actionBar.selected,
                { items: totalSelected, count: totalSelected }
              )}
              deleteTooltip={
                TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.actionBar.delete
              }
              onPressDelete={handleDeleteRow}
              onPressX={closeActionBar}
            />
          </div>
        )}
        {renderToast()}
      </div>
    </div>
  );
};

export default SubscriptionByFileTable;
